import { ArrowRight, FileText, BarChart, Users, Award, BookOpen, Database, Upload, ClipboardCheck, GitBranch, Activity, FolderHeart, CalendarHeart, Scale, ListChecks, TrendingUp } from 'lucide-react';
import Hero from '../components/Hero';
import SectionHeading from '../components/SectionHeading';
import FeatureCard from '../components/FeatureCard';
import TestimonialCard from '../components/TestimonialCard';
import CallToAction from '../components/CallToAction';
import EmailSignup from '../components/EmailSignup';
import { motion } from 'framer-motion';

const Home = () => {
  return (
    <>
      <Hero
        title="Sheep Management Made Simple"
        subtitle="Record, manage, analyse your sheep flock in a simple way."
        ctaText="Launch PedigreePro App"
        ctaLink="https://pedigreepro.netlify.app/"
        secondaryCtaText="Learn More"
        secondaryCtaLink="/what-it-does"
        image="/images/flock1.jpg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
      />

      <section className="section bg-white">
        <div className="container">
          <SectionHeading 
            title="Simplify Your Sheep Breeding Program"
            subtitle="PedigreePro provides all the tools you need to manage your flock with ease and precision."
          />
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <FeatureCard 
              icon={<FileText size={24} />}
              title="Complete Records"
              description="No matter how small or large your flock, we've got it covered. From Movement and Medical records to offspring and pedigrees, PedigreePro is just the tool you need."
              delay={0.1}
            />
            <FeatureCard 
              icon={<BarChart size={24} />}
              title="Default profile"
              description="Simply log in to the program, and set up your profile, then this will act as the default for simplifying animal entry. You can edit your profile at any time."
              delay={0.2}
            />
            <FeatureCard 
              icon={<Database size={24} />}
              title="Secure Data Storage"
              description="Your valuable flock data is securely stored and accessible from anywhere with automatic backups."
              delay={0.3}
            />
            <FeatureCard 
              icon={<Users size={24} />}
              title="Integration"
              description="Enter ALL your animals and then simply output selected ones for registration in the correct file format for your breed society. No double entry required."
              delay={0.4}
            />
            <FeatureCard 
              icon={<BookOpen size={24} />}
              title="FREE"
              description="PedigreePro is completely free, no set-up costs, just log in. If you run a small flock, this is your ideal solution. However, as you flock grows past 20 animals, there is a small monthly subsciption charge to make it unlimited."
              delay={0.5}
            />
            <FeatureCard
              icon={<Award size={24} />}
              title="Show Management"
              description="It even keeps track of the shows you have been to and counts your prize money for you! "
              delay={0.6}
            />
            <FeatureCard
              icon={<Upload size={24} />}
              title="NEW - Smart Import Technology"
              description="Import your records from main pedigree certificates. Yes, we have now implemented some very clever technology to import sheep from a printout from your sheep society database. A 5-generation pedigree will import 31 sheep, in a few seconds. No longer the need to type in all that back history - just import it!"
              delay={0.7}
            />
            <FeatureCard
              icon={<ClipboardCheck size={24} />}
              title="NEW - Sheep Assessment Function"
              description="Now you can assess your animals for 14 characteristics at the touch of a button even out in the field. Perfect for pedigree animals, making it easy to track and evaluate breeding quality wherever you are."
              delay={0.8}
            />
            <FeatureCard
              icon={<GitBranch size={24} />}
              title="NEW - Matrilineal Family Lines"
              description="Trace the complete female family line of any ewe back to her founding matriarch — just as the great pioneers like Robert Bakewell did with pen and paper, but now at the touch of a button. Export to PDF for your records."
              delay={0.9}
            />
            <FeatureCard
              icon={<Activity size={24} />}
              title="NEW - Inbreeding Check"
              description="Select any ram — including a prospective purchase — and instantly see Wright's inbreeding coefficient for potential offspring with every ewe in your flock. Colour-coded risk ratings help you make safe, informed breeding decisions."
              delay={1.0}
            />
            <FeatureCard
              icon={<FolderHeart size={24} />}
              title="NEW - Family Groups"
              description="Organise your animals into named family groups, browse all animals in each family, and export a complete multi-family spreadsheet with one click. A powerful new way to view your flock through its breeding history."
              delay={1.1}
            />
            <FeatureCard
              icon={<CalendarHeart size={24} />}
              title="NEW - Tupping Records"
              description="Enter the tupping details for each individual ewe, including the ram used and the date, then output your tupping calendar with lambing due dates. Plan your lambing season with confidence."
              delay={1.2}
            />
            <FeatureCard
              icon={<Scale size={24} />}
              title="NEW - Weight Recording"
              description="Record birth, weaning, slaughter and other weights for every animal, with an optional condition score and a carcass grade for slaughter weights. PedigreePro shows each animal's age, gain and daily gain automatically, in kilograms or pounds."
              delay={1.3}
            />
            <FeatureCard
              icon={<ListChecks size={24} />}
              title="NEW - Weigh the Flock in the Shed"
              description="Pick the date, filter your animals by location, year of birth or gender, and type the weights straight down the list. Sort by any column, save the whole group in one go, and export every lamb weighing between two dates to Excel. Works on a phone or tablet too."
              delay={1.4}
            />
            <FeatureCard
              icon={<TrendingUp size={24} />}
              title="NEW - Weight & Profit Analysis"
              description="Find out which ewes and rams are producing your best lambs. A Premium Excel report gives you a ewe scorecard with barren ewes flagged and kilograms of lamb weaned per ewe, a ram progeny table, lamb growth compared like for like, and revenue from sales."
              delay={1.5}
            />
          </div>
        </div>
      </section>
      
      <section className="section bg-neutral-50">
        <div className="container">
          <SectionHeading 
            title="Developed by sheep breeders for sheep breeders"
            subtitle="See what some breeders are saying about PedigreePro."
          />
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <TestimonialCard 
              quote="PedigreePro is just what I have been looking for."
              author="Shirley Lewis"
              role="Ryeland sheep breeder, Wales"
              delay={0.1}
            />
            <TestimonialCard 
              quote="At last, a simple package to keep all my records in one place."
              author="Ron McCall"
              role="Valais Blacknose breeder, Scotland"
              delay={0.2}
            />
            <TestimonialCard 
              quote="I made the simple switch from a more expensive and complicated package, transferring my data over to this simple to use software that covers all my requirements ."
              author="Heather Innes"
              role="Commercial sheep breeder, Ireland"
              delay={0.3}
            />
          </div>
          
          <motion.div 
            className="mt-12 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <a href="/what-it-does" className="inline-flex items-center gap-2 text-primary font-medium hover:text-primary-dark transition-colors">
              See more about what PedigreePro can do
              <ArrowRight size={18} />
            </a>
          </motion.div>
        </div>
      </section>
      
      <EmailSignup />

      <CallToAction 
        title="Ready to Transform Your Sheep Management?"
        description="Join hundreds of small and large sheep breeders who have improved their record keeping with PedigreePro."
        buttonText="Launch PedigreePro App"
        buttonLink="https://pedigreepro.netlify.app/"
        secondaryButtonText="View Subscription Plans"
        secondaryButtonLink="/subscription"
      />
    </>
  );
};

export default Home;